<template>
    <div class="NonExtractionReview">
        <div class="NonExtractionReview__container">
            <div class="NonExtractionReview__form">
                <Table class="NonExtractionReview__table">
                    <template #head>
                        <HeadCell
                            data-test-id="NonExtractionReview__fieldHeader"
                            class="NonExtractionReview__fieldHeader"
                        >
                            Field
                        </HeadCell>
                        <HeadCell data-test-id="NonExtractionReview__valueHeader">
                            Value
                        </HeadCell>
                        <HeadCell
                            data-test-id="NonExtractionReview__decisionHeader"
                            class="NonExtractionReview__decisionHeader"
                        >
                            Decision
                        </HeadCell>
                    </template>
                    <Row
                        v-for="(actionContentLabel, index) in reviewContentLabels"
                        :key="`${actionDetails.field}-${index}`"
                        hoverable
                    >
                        <Cell
                            data-test-id="NonExtractionReview__fieldRowCell"
                            class="NonExtractionReview__fieldRowCell"
                        >
                            {{ actionField }}
                        </Cell>
                        <Cell data-test-id="NonExtractionReview__valueRowCell">
                            <span>
                                {{ actionContentLabel }}
                            </span>
                        </Cell>
                        <Cell
                            data-test-id="NonExtractionReview__decisionRowCell"
                            class="NonExtractionReview__decisionRowCell"
                        >
                            <!-- A decided action shows the verdict recorded for this requirement. -->
                            <span v-if="readOnly" class="NonExtractionReview__recordedVerdict">
                                {{ recordedVerdictFor(actionDetails.requirements[index]) }}
                            </span>
                            <RbacDisableDefaultGuard v-else :features="[RbacFeature.actionsResolveEntityAction]">
                                <Dropdown
                                    :selected="selectedOptions"
                                    :options="options"
                                    placeholder="Select Status"
                                    @input="selectOption($event, actionDetails.requirements[index])"
                                />
                            </RbacDisableDefaultGuard>
                        </Cell>
                    </Row>
                </Table>
                <div
                    v-if="showRecollectionDate"
                    class="NonExtractionReview__recollectionDate"
                >
                    <label class="NonExtractionReview__recollectionDateLabel">
                        This document will need to be re-collected on
                    </label>
                    <span v-if="readOnly" class="NonExtractionReview__recordedRecollectionDate">
                        {{ recordedRecollectionDate }}
                    </span>
                    <RbacDisableDefaultGuard v-else :features="[RbacFeature.actionsResolveEntityAction]">
                        <DatePicker
                            data-test-id="NonExtractionReview__recollectionDatePicker"
                            placeholder="Select Date"
                            :value="recollectionDate"
                            @input="onRecollectionDateInput"
                        />
                    </RbacDisableDefaultGuard>
                    <p class="NonExtractionReview__recollectionDateHint">
                        Accepting requires a re-collection date. This becomes the expiration date for
                        this submission.
                    </p>
                    <p
                        v-if="isRecollectionDateInPast"
                        data-test-id="NonExtractionReview__pastDateWarning"
                        class="NonExtractionReview__pastDateWarning"
                        role="alert"
                    >
                        This date has already passed, so the entity will be evaluated as non-compliant
                        as soon as this action is resolved.
                    </p>
                </div>
                <p
                    v-else-if="actionDetails.recollectionDateRequired && isRejecting"
                    data-test-id="NonExtractionReview__rejectionHint"
                    class="NonExtractionReview__recollectionDateHint"
                >
                    No re-collection date is needed to reject this document.
                </p>
                <div v-if="!readOnly" class="flex justify-end gap-3 p-4">
                    <NewButton
                        data-test-id="NonExtractionReview__cancelButton"
                        variant="outline"
                        color="danger"
                        @click="onCancel"
                    >
                        Cancel
                    </NewButton>
                    <RbacDisableDefaultGuard :features="[RbacFeature.actionsResolveEntityAction]">
                        <NewButton
                            class="NonExtractionReview__saveButton"
                            data-test-id="NonExtractionReview__saveButton"
                            :disabled="saveDisabled"
                            variant="outline"
                            color="primary"
                            @click="onSave"
                        >
                            Save
                        </NewButton>
                    </RbacDisableDefaultGuard>
                </div>
            </div>

            <RbacDisplayGuard :features="[RbacFeature.actionsViewDocument]">
                <div class="NonExtractionReview__documents">
                    <LogoLoader v-if="loading" />
                    <DocumentsViewer v-if="!loading" :documents="docsStatus.list" />
                </div>
            </RbacDisplayGuard>
        </div>
    </div>
</template>

<style lang="scss">
    .NonExtractionReview {
        &__recollectionDate {
            padding: 0 16px 16px;
        }

        &__recollectionDateLabel {
            display: block;
            margin-bottom: 8px;
            font-size: var(--text-body-size, 14px);
        }

        &__recollectionDateHint {
            margin-top: 8px;
            padding: 0 16px 16px;
            font-size: var(--text-body-size, 14px);
            color: var(--eidSecureBlue-75);
        }

        &__recollectionDate &__recollectionDateHint {
            padding: 0;
        }

        &__pastDateWarning {
            margin-top: 8px;
            padding: 10px 8px;
            font-size: var(--text-body-size, 14px);
            color: var(--eidDangerRed);
            background-color: var(--eidDangerRed-10);
            border: 1px solid var(--eidDangerRed-25);
            border-radius: 4px;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import startCase from 'lodash/startCase';
    import moment from 'moment';

    import { DatePicker } from '@evidentid/dashboard-commons/components/DatePicker';
    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import {
        ActionType,
        NonExtractionCriterionResolutionResolveInput,
        ActionReview,
        NonExtractionResolutionActionDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import DocumentsViewer from '@/modules/entity-actions-review/components/DocumentsViewer/DocumentsViewer.vue';
    import { WRONG_ACTION_DETAILS_TYPE_MESSAGE } from '@/modules/entity-actions-review/constants';
    import { CoiDocumentStatus, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';

    export default defineComponent({
        name: 'NonExtractionReview',
        components: {
            RbacDisplayGuard,
            RbacDisableDefaultGuard,
            NewButton,
            Dropdown,
            DatePicker,
            DocumentsViewer,
            HeadCell,
            LogoLoader,
            Table,
            Cell,
            Row,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                default: () => ({} as ActionReview),
            },
            readOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'cancel', 'resolve' ],
        data() {
            return {
                selectedOptions: [],
                options: [ { label: 'Accept', value: true }, { label: 'Reject', value: false } ],
                showConfirmationModal: false,
                requirementResults: {} as Record<string, boolean | undefined>,
                recollectionDate: null as string | null,
                RbacFeature,
            };
        },
        computed: {
            /** The stored resolve payload, which holds one verdict per requirement plus the date. */
            recordedResult(): Record<string, unknown> {
                const result = this.actionReview.resolution?.result;
                return typeof result === 'object' && result !== null ? result as Record<string, unknown> : {};
            },
            recordedRecollectionDate(): string {
                const date = this.recordedResult.recollectionDate;
                return typeof date === 'string' && date ? date : 'Not recorded';
            },
            rpName(): string {
                return this.$rp.current!;
            },
            actionDetails(): NonExtractionResolutionActionDetails {
                return typeNarrowing(
                    this.actionReview.action,
                    this.actionReview.action.$action === ActionType.nonExtractionCriterionResolutionV1,
                    WRONG_ACTION_DETAILS_TYPE_MESSAGE,
                );
            },
            docsStatus(): CoiDocumentStatus {
                if (useEntityActionsReviewStore().rprDocuments[this.rpName] &&
                    useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id]) {
                    return useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id];
                } else {
                    return { status: OperationStatus.uninitialized, list: [] };
                }
            },
            requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
                return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions || {};
            },
            loading(): boolean {
                return this.docsStatus.status === OperationStatus.loading;
            },
            isAccepting(): boolean {
                const results = this.actionDetails.requirements.map(
                    (requirement: string) => this.requirementResults[requirement],
                );
                return results.length > 0 && results.every((result) => result === true);
            },
            /* `isAccepting` reads the reviewer's in-progress selections, which stay empty in read-only
               mode -- nothing seeds them from the stored decision. Read-only therefore asks the recorded
               payload instead, so a completed action that was accepted with a re-collection date still
               shows that date rather than omitting the row entirely. */
            showRecollectionDate(): boolean {
                if (!this.actionDetails.recollectionDateRequired) {
                    return false;
                }
                return this.readOnly ? this.recordedRecollectionDate !== 'Not recorded' : this.isAccepting;
            },
            isRejecting(): boolean {
                const results = this.actionDetails.requirements.map(
                    (requirement: string) => this.requirementResults[requirement],
                );
                return results.length > 0 && results.every((result) => result !== undefined) &&
                    results.some((result) => result === false);
            },
            isRecollectionDateInPast(): boolean {
                return Boolean(this.recollectionDate) && moment(this.recollectionDate).isBefore(moment(), 'day');
            },
            saveDisabled(): boolean {
                const missingDecision = this.actionDetails.requirements.some(
                    (requirement: string) => !(requirement in this.requirementResults) ||
                        this.requirementResults[requirement] === undefined,
                );
                return missingDecision || (this.showRecollectionDate && !this.recollectionDate);
            },
            actionField(): string {
                return this.actionDetails.field && startCase(this.actionDetails.field) || '-';
            },
            reviewContentLabels(): string[] {
                return this.actionDetails.requirements.map((actionRequiredMsg: string) => {
                    if (this.actionDetails.field === 'documentVerification') {
                        const reqLabel = this.actionDetails.coverageType
                            ? (this.requirementDefinitions?.[this.actionDetails.coverageType]?.defaultLabel
                                || startCase(this.actionDetails.coverageType.toLowerCase()))
                            : null;
                        return reqLabel ? `${actionRequiredMsg} - ${reqLabel}` : actionRequiredMsg;
                    }
                    return actionRequiredMsg;
                });
            },
        },
        watch: {
            actionReview: {
                immediate: true,
                handler() {
                    if (this.docsStatus.status === OperationStatus.uninitialized &&
                        this.actionReview.evaluationResults.length > 0 &&
                        this.actionDetails.verificationId) {
                        useEntityActionsReviewStore().loadRprDocuments({
                            rpName: this.rpName,
                            actionId: this.actionReview.id,
                            documentsFor: [ {
                                verificationId: this.actionDetails.verificationId,
                                requirementType: this.actionDetails.coverageType,
                            } ],
                        });
                    }
                },
            },
        },
        methods: {
            recordedVerdictFor(requirement: string): string {
                const results = this.recordedResult.requirementResults;
                const verdict = typeof results === 'object' && results !== null
                    ? (results as Record<string, unknown>)[requirement]
                    : undefined;
                if (verdict === true) {
                    return 'Accept';
                }
                return verdict === false ? 'Reject' : 'Not recorded';
            },
            onSave(): void {
                if (!this.saveDisabled) {
                    const data: NonExtractionCriterionResolutionResolveInput = {
                        $action: ActionType.nonExtractionCriterionResolutionV1,
                        requirementResults: this.requirementResults,
                    };
                    // A date is only valid alongside an accept; sending one with a rejection is a 400.
                    if (this.showRecollectionDate && this.recollectionDate) {
                        data.recollectionDate = this.recollectionDate;
                    }
                    this.$emit('resolve', data);
                }
            },
            onCancel(): void {
                this.$emit('cancel');
            },
            selectOption(option: DropdownOption, requirement: string): void {
                this.requirementResults = { ...this.requirementResults, [requirement]: option.value };
            },
            onRecollectionDateInput(date: string | null): void {
                this.recollectionDate = date;
            },
        },
    });
</script>
